import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import UIButtonGroup from 'UIComponents/button/UIButtonGroup';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UIList from 'UIComponents/list/UIList';
import UISection from 'UIComponents/section/UISection';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UINotificationBadge from 'UIComponents/badge/UINotificationBadge';
import { CANDY_APPLE, MARIGOLD, OLAF } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import UILink from 'UIComponents/link/UILink';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ICON_SIZE = 12;
const CIRCULAR_PADDING = 0.3;
const BADGE_DESCRIPTION = 'epIssueButton.badgeDescription';
const PopoverContent = styled.div.withConfig({
  displayName: "EPIssueButton__PopoverContent",
  componentId: "io7kc8-0"
})(["max-height:50vh;overflow-y:auto;"]);
const Issues = ({
  issues,
  iconBgColor,
  issueLabel,
  onIssueClick
}) => {
  return /*#__PURE__*/_jsxs(UISection, {
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(UIMediaObject, {
        className: "m-left-8",
        itemLeft: /*#__PURE__*/_jsx(UIIconCircle, {
          name: "warning",
          size: ICON_SIZE,
          padding: CIRCULAR_PADDING,
          color: OLAF,
          backgroundColor: iconBgColor
        }),
        children: /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: issueLabel
          })
        })
      })
    }), /*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(UIList, {
        ordered: true,
        styled: true,
        childClassName: "m-left-2 m-right-2",
        children: issues.map((issue, index) => {
          const {
            node
          } = issue;
          return /*#__PURE__*/_jsx(UILink, {
            use: "unstyled",
            onClick: () => onIssueClick(issue),
            children: node
          }, index);
        })
      })
    })]
  });
};
const EPIssueButton = ({
  actions,
  buttonSize = 'small',
  children,
  'data-test': dataTest,
  disabled = false,
  errorLabel = 'epIssueButton.errorLabel',
  errors,
  loading = false,
  loadingFailed = false,
  onButtonClick,
  onDropdownOpenComplete,
  onIssueClick,
  onMouseEnter,
  popoverPlacement = 'bottom left',
  popoverWidth = 300,
  tooltipOverride,
  warningLabel = 'epIssueButton.warningLabel',
  warnings
}) => {
  const [canPopupOpen, setCanPopupOpen] = useState(false);
  function handleButtonClick() {
    setCanPopupOpen(false);
    onButtonClick();
  }
  function hasIssues() {
    return !!errors.length || !!warnings.length;
  }
  function handleMouseEnterButton() {
    if (hasIssues()) {
      setCanPopupOpen(true);
    }
    if (onMouseEnter) {
      onMouseEnter();
    }
  }
  function renderPopOverContent() {
    return /*#__PURE__*/_jsxs(PopoverContent, {
      className: "m-top-8",
      children: [!!errors.length && /*#__PURE__*/_jsx(Issues, {
        issues: errors,
        iconBgColor: CANDY_APPLE,
        issueLabel: errorLabel,
        onIssueClick: onIssueClick
      }), !!warnings.length && /*#__PURE__*/_jsx(Issues, {
        issues: warnings,
        iconBgColor: MARIGOLD,
        issueLabel: warningLabel,
        onIssueClick: onIssueClick
      })]
    });
  }
  function renderIssueButton() {
    return /*#__PURE__*/_jsx(UINotificationBadge, {
      badgeDescription: I18n.text(BADGE_DESCRIPTION),
      badgeLabel: errors.length,
      showBadge: !!errors.length && !tooltipOverride,
      "data-test": dataTest,
      "data-test-num-errors": errors.length,
      style: {
        whiteSpace: 'nowrap'
      },
      children: /*#__PURE__*/_jsxs(UIButtonGroup, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          disabled: !!errors.length || disabled,
          use: "primary",
          size: buttonSize,
          onClick: handleButtonClick,
          onMouseEnter: handleMouseEnterButton,
          loading: loading,
          failed: loadingFailed,
          children: children
        }), actions && actions.length > 0 && /*#__PURE__*/_jsx(UIDropdown, {
          "data-test-id": "issue-dropdown",
          buttonUse: "primary",
          buttonText: /*#__PURE__*/_jsx(UIIcon, {
            name: "down"
          }),
          buttonClassName: "p-x-1",
          buttonSize: buttonSize,
          caretRenderer: null,
          disabled: !!errors.length || disabled || loading,
          onOpenComplete: onDropdownOpenComplete,
          style: {
            borderLeft: `1px solid rgba(255, 255, 255, 0.4)`
          },
          children: /*#__PURE__*/_jsx(UIList, {
            children: actions.map((action, index) => {
              const {
                label,
                onClick,
                id
              } = action;
              return /*#__PURE__*/_jsx(UIButton, {
                "data-test-id": id,
                onClick: onClick,
                children: label
              }, index);
            })
          })
        })]
      })
    });
  }
  if (tooltipOverride) {
    return /*#__PURE__*/_jsx(UITooltip, {
      placement: popoverPlacement,
      title: tooltipOverride,
      children: renderIssueButton()
    });
  }
  return /*#__PURE__*/_jsx(UIPopover, {
    open: canPopupOpen && hasIssues(),
    closeOnOutsideClick: true,
    onOpenChange: () => setCanPopupOpen(false),
    onFocusLeave: () => setCanPopupOpen(false),
    placement: popoverPlacement,
    width: popoverWidth,
    autoPlacement: false,
    Content: renderPopOverContent,
    "data-test-id": "issue-popover",
    children: renderIssueButton()
  });
};
EPIssueButton.propTypes = {
  actions: PropTypes.array,
  buttonSize: PropTypes.string,
  children: PropTypes.node.isRequired,
  'data-test': PropTypes.string,
  disabled: PropTypes.bool,
  errorLabel: PropTypes.string,
  errors: PropTypes.array.isRequired,
  onButtonClick: PropTypes.func.isRequired,
  onDropdownOpenComplete: PropTypes.func,
  onIssueClick: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  popoverPlacement: PropTypes.string,
  popoverWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltipOverride: PropTypes.node,
  warningLabel: PropTypes.string,
  warnings: PropTypes.array.isRequired
};
export default EPIssueButton;